// ============================================================
// DRUM ELIGIBILITY - may this plate be pressed, and if not, why?
//
// Lifted out of DeathWatchSimulator in v8.300.0. It was a useMemo over
// component state, which meant the only way to assert a reason was to
// drive the whole app; the reasons are strings a player reads off a
// dimmed plate, so they belong somewhere a test can name them.
//
// ONE reason per plate - the first unmet requirement, in the order
// below. The order is deliberate: a locked panel outranks a wrong
// weapon, which outranks a missing target, because that is the order a
// player can DO something about them in.
//
// Pure. Zero React dependency.
// ============================================================
import type { ActionDef } from './types';
import { CHARGE_REASONS, type ChargeRefusal } from '../battlefield/charge';

export const DRUM_REASONS = {
  locked: 'Attack controls are locked',
  melee: 'Melee weapon required',
  ranged: 'Ranged weapon required',
  fullAuto: 'Full-auto fire mode required',
  noActor: 'No acting character',
  noTarget: 'No target',
  targetDead: 'The target is dead',
  notProne: 'You are not prone',
  noBearing: 'Aim the tape or pick a target first',
  notUnaware: 'Nobody here is unaware',
  notUnawareKnown: 'They know you are here: set someone Unaware by hand',
  outOfReach: 'Out of melee reach',
  notInContact: 'Not in contact with the target',
  chargeCannotMove: CHARGE_REASONS.cannotMove,
  chargeCrawling: CHARGE_REASONS.crawling,
  chargeTooClose: CHARGE_REASONS.tooClose,
  chargeTooFar: CHARGE_REASONS.tooFar,
  chargeBlocked: CHARGE_REASONS.blocked,
  chargeClimb: CHARGE_REASONS.climb,
  offBow: 'The target is not off the bow',
  pinned: 'Pinned: snap fire, crawl or go prone',
  alreadyProne: 'You are already prone',
  notEngaged: 'Not engaged in melee',
} as const;

/** Each charge refusal's reason. */
const CHARGE_KEYS: Record<ChargeRefusal, keyof typeof DRUM_REASONS> = {
  cannotMove: 'chargeCannotMove',
  crawling: 'chargeCrawling',
  tooClose: 'chargeTooClose',
  tooFar: 'chargeTooFar',
  blocked: 'chargeBlocked',
  climb: 'chargeClimb',
};

/** What the drum needs to know about the world, and no more. */
export interface DrumContext {
  /** The attack controls are locked by the GM's permissions. */
  locked: boolean;
  isMelee: boolean;
  /** A Character is acting - a plain statblock cannot roll an opposed test. */
  hasActor: boolean;
  hasTarget: boolean;
  targetIsDead: boolean;
  actorIsProne: boolean;
  /** The armed weapon is set to full auto. */
  isFullAuto: boolean;
  /** A direction exists to fire along: the measuring tape, or a target. */
  hasBearing: boolean;
  /** The current target has not noticed the party (engine/unaware; Stealth's ladder). Absent = it has. */
  targetUnaware?: boolean;
  /** Stealth's ladder is at Searching or above: nobody following it is unaware, only whom the GM sets so. */
  ladderKnows?: boolean;
  /** The armed melee weapon does not reach the target, by the map (engine/battlefield/reach). Absent = it does. */
  outOfReach?: boolean;
  /** Why a charge at the target cannot be run, by the map (engine/battlefield/charge). Absent = it can, or no map. */
  chargeRefusal?: ChargeRefusal | null;
  /** The actor's hull is not in contact with the target, by the map (engine/actions/ram). Absent = it is. */
  notInContact?: boolean;
  /** The target is not off the actor's bow, by the map. Absent = it is, or the map cannot say. */
  offBow?: boolean;
  /** The actor is Pinned: only the plates marked `whilePinned` may be pressed (2026-10-26). Absent = he is not. */
  actorIsPinned?: boolean;
  /** The actor is engaged with no enemy, by the map. Absent = he is, or the map cannot say (positions off). */
  notEngaged?: boolean;
}

/**
 * id -> why it cannot be pressed. An id ABSENT from the record is
 * pressable, which is the shape every caller already expects.
 */
export function drumDisabledReasons(defs: ActionDef[], ctx: DrumContext): Record<string, string> {
  const out: Record<string, string> = {};
  for (const def of defs) {
    const reason = firstUnmet(def, ctx);
    if (reason) out[def.id] = reason;
  }
  return out;
}

function firstUnmet(def: ActionDef, ctx: DrumContext): string | null {
  const unmet = REQUIREMENTS.find(([fails]) => fails(def, ctx));
  if (!unmet) return null;
  const [, reason] = unmet;
  return DRUM_REASONS[typeof reason === 'function' ? reason(ctx) : reason];
}

type ReasonKey = keyof typeof DRUM_REASONS;

/** Each requirement and the reason a plate dims for it - or how to pick one - in the order they are asked. */
const REQUIREMENTS: [(def: ActionDef, ctx: DrumContext) => boolean, ReasonKey | ((ctx: DrumContext) => ReasonKey)][] = [
  [(_def, ctx) => !!ctx.locked, 'locked'],
  [(def, ctx) => !!ctx.actorIsPinned && !def.whilePinned, 'pinned'],
  [(def, ctx) => !!def.requiresMelee && !ctx.isMelee, 'melee'],
  [(def, ctx) => !!def.requiresRanged && !!ctx.isMelee, 'ranged'],
  [(def, ctx) => !!def.requiresFullAuto && !ctx.isFullAuto, 'fullAuto'],
  [(def, ctx) => !!def.requiresActor && !ctx.hasActor, 'noActor'],
  [(def, ctx) => !!def.requiresProne && !ctx.actorIsProne, 'notProne'],
  [(def, ctx) => !!def.requiresUpright && !!ctx.actorIsProne, 'alreadyProne'],
  [(def, ctx) => !!def.requiresEngaged && !!ctx.notEngaged, 'notEngaged'],
  [(def, ctx) => !!def.requiresBearing && !ctx.hasBearing, 'noBearing'],
  [(def, ctx) => !!def.requiresTarget && !ctx.hasTarget, 'noTarget'],
  [(def, ctx) => !!def.requiresTarget && !!ctx.targetIsDead, 'targetDead'],
  [
    (def, ctx) => !!def.requiresUnawareTarget && !ctx.targetUnaware,
    (ctx) => (ctx.ladderKnows ? 'notUnawareKnown' : 'notUnaware'),
  ],
  [(def, ctx) => !!def.requiresReach && !!ctx.outOfReach, 'outOfReach'],
  [(def, ctx) => !!def.requiresChargeLane && !!ctx.chargeRefusal, (ctx) => CHARGE_KEYS[ctx.chargeRefusal!]],
  [(def, ctx) => !!def.requiresContact && !!ctx.notInContact, 'notInContact'],
  [(def, ctx) => !!def.requiresBow && !!ctx.offBow, 'offBow'],
];
